import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard,
  Globe,
  FileWarning,
  Mail,
  AppWindow,
  Monitor,
  Users,
  Zap,
  FileText,
  Settings,
  CreditCard,
  LogOut,
  ShieldAlert,
  Building2,
  Download,
  Server,
  Webhook,
  Crown,
  UserCog,
  Headphones,
  Briefcase,
  Tag,
  Plug,
  ScrollText,
  Gift,
  ChevronDown,
  ChevronRight,
  Shield,
  Scan,
  Plus,
  Search,
  Bell,
  AlertTriangle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Logo } from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import NotificationCenter from './NotificationCenter';
import { ADMIN_BASE_URL } from '../config/admin';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// Quick Actions for the top section
const quickActions = [
  { icon: Scan, label: 'Scan File', action: 'scan-file', color: 'text-blue-400' },
  { icon: Search, label: 'Check Domain', action: 'check-domain', color: 'text-green-400' },
  { icon: Plus, label: 'Add Device', action: 'add-device', color: 'text-purple-400' },
];

// Expandable nav group component
const NavGroup = ({ icon: Icon, label, children, defaultOpen = false, badge = null, badgeColor = 'bg-blue-500' }) => {
  const location = useLocation();
  
  // Check if any child is active - compute initial state
  const childPaths = React.Children.toArray(children)
    .filter(child => child?.props?.to)
    .map(child => child.props.to);
  
  const isChildActive = childPaths.some(path => 
    location.pathname === path || location.pathname.startsWith(path + '/')
  );
  
  const [isOpen, setIsOpen] = useState(defaultOpen || isChildActive);
  
  // Update when route changes - only expand, never collapse automatically
  useEffect(() => {
    if (isChildActive && !isOpen) {
      setIsOpen(true);
    }
    // eslint-disable-next-line
  }, [isChildActive]);

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
          isOpen ? "bg-slate-800/50 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
        )}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <span>{label}</span>
          {badge !== null && badge > 0 && (
            <span className={cn("px-1.5 py-0.5 text-xs font-bold rounded-full text-white", badgeColor)}>
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {isOpen && (
        <div className="ml-4 pl-3 border-l border-slate-800 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
};

// Single nav item component
const NavItem = ({ to, icon: Icon, label, badge = null, badgeColor = 'bg-blue-500', end = false }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      cn(
        "flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors",
        isActive
          ? "bg-blue-500/10 text-blue-500"
          : "text-slate-400 hover:text-white hover:bg-slate-800"
      )
    }
  >
    <div className="flex items-center gap-3">
      {Icon && <Icon className="w-5 h-5" />}
      <span>{label}</span>
    </div>
    {badge !== null && badge > 0 && (
      <span className={cn("px-1.5 py-0.5 text-xs font-bold rounded-full text-white", badgeColor)}>
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </NavLink>
);

// Sub nav item (inside groups)
const SubNavItem = ({ to, label, badge = null, badgeColor = 'bg-slate-600', end = false }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      cn(
        "flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
        isActive
          ? "bg-blue-500/10 text-blue-400 font-medium"
          : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/50"
      )
    }
  >
    <span>{label}</span>
    {badge !== null && badge > 0 && (
      <span className={cn("px-1.5 py-0.5 text-xs font-medium rounded-full text-white", badgeColor)}>
        {badge}
      </span>
    )}
  </NavLink>
);

// Admin navigation - filtered by role
const getAdminNavItems = (userRole) => {
  const items = [
    { to: ADMIN_BASE_URL, icon: LayoutDashboard, label: 'Overview', exact: true, roles: ['super_admin', 'support', 'billing_admin'] },
    { to: `${ADMIN_BASE_URL}/companies`, icon: Building2, label: 'Companies', roles: ['super_admin', 'support', 'billing_admin'] },
    { to: `${ADMIN_BASE_URL}/msps`, icon: Briefcase, label: 'MSPs', roles: ['super_admin'] },
    { to: `${ADMIN_BASE_URL}/system-users`, icon: UserCog, label: 'System Users', roles: ['super_admin'] },
    { to: `${ADMIN_BASE_URL}/audit-logs`, icon: ScrollText, label: 'Audit Logs', roles: ['super_admin'] },
    { to: `${ADMIN_BASE_URL}/billing`, icon: CreditCard, label: 'Billing', roles: ['super_admin', 'billing_admin'] },
    { to: `${ADMIN_BASE_URL}/promotions`, icon: Tag, label: 'Promotions', roles: ['super_admin'] },
    { to: `${ADMIN_BASE_URL}/support`, icon: Headphones, label: 'Support', roles: ['super_admin', 'support'] },
    { to: `${ADMIN_BASE_URL}/health`, icon: Server, label: 'System Health', roles: ['super_admin'] },
  ];
  
  return items.filter(item => item.roles.includes(userRole));
};

export const DashboardLayout = ({ children }) => {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Threat counts state
  const [threatCounts, setThreatCounts] = useState({
    dns: 0,
    files: 0,
    emails: 0,
    apps: 0,
    total: 0
  });
  
  // Check if we're on admin pages
  const isAdminPage = location.pathname.startsWith(ADMIN_BASE_URL) || location.pathname.startsWith('/admin');
  const isSystemUser = ['super_admin', 'support', 'billing_admin'].includes(user?.role);

  // Fetch threat counts
  useEffect(() => {
    const fetchThreatCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch counts from different threat endpoints
        const [dnsRes, filesRes, emailsRes, appsRes] = await Promise.allSettled([
          axios.get(`${API}/api/threats/dns?limit=1`, { headers }),
          axios.get(`${API}/api/threats/files?limit=1`, { headers }),
          axios.get(`${API}/api/threats/emails?limit=1`, { headers }),
          axios.get(`${API}/api/threats/apps?limit=1`, { headers }),
        ]);
        
        // Count critical/high threats (simplified - using array length for demo)
        const dns = dnsRes.status === 'fulfilled' ? 
          (dnsRes.value.data?.filter?.(t => t.classification === 'malicious')?.length || 0) : 0;
        const files = filesRes.status === 'fulfilled' ? 
          (filesRes.value.data?.filter?.(t => ['critical', 'high'].includes(t.classification))?.length || 0) : 0;
        const emails = emailsRes.status === 'fulfilled' ? 
          (emailsRes.value.data?.filter?.(t => t.status === 'quarantined')?.length || 0) : 0;
        const apps = appsRes.status === 'fulfilled' ? 
          (appsRes.value.data?.filter?.(t => ['critical', 'high'].includes(t.classification))?.length || 0) : 0;
        
        setThreatCounts({
          dns,
          files,
          emails,
          apps,
          total: dns + files + emails + apps
        });
      } catch (error) {
        console.error('Failed to fetch threat counts:', error);
      }
    };
    
    fetchThreatCounts();
    // Refresh every 60 seconds
    const interval = setInterval(fetchThreatCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'scan-file':
        navigate('/files');
        break;
      case 'check-domain':
        navigate('/dns');
        break;
      case 'add-device':
        navigate('/devices');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 border-r border-slate-800 bg-slate-900/50 backdrop-blur-xl hidden md:flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <Logo size="default" showSubtext={true} subtext="Cybersecurity Platform" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          {/* Admin Navigation - for system users on admin pages */}
          {isSystemUser && isAdminPage ? (
            <div className="space-y-1">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-3 flex items-center gap-2">
                <Crown className="w-3 h-3" />
                Administration
              </div>
              {getAdminNavItems(user?.role).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-purple-500/10 text-purple-500 border-l-2 border-purple-500 -ml-px pl-[11px]'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`
                  }
                  data-testid={`nav-admin-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ) : (
            <>
              {/* Quick Actions */}
              <div className="mb-6">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-3">
                  Quick Actions
                </div>
                <div className="grid grid-cols-3 gap-2 px-1">
                  {quickActions.map((action) => (
                    <button
                      key={action.action}
                      onClick={() => handleQuickAction(action.action)}
                      className="flex flex-col items-center gap-1 p-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
                      title={action.label}
                    >
                      <action.icon className={cn("w-4 h-4", action.color, "group-hover:scale-110 transition-transform")} />
                      <span className="text-[10px] text-slate-500 group-hover:text-slate-400">{action.label.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Navigation */}
              <div className="space-y-1">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-3">
                  Navigation
                </div>
                
                {/* Dashboard */}
                <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" end />
                
                {/* Threat Center - Expandable */}
                <NavGroup 
                  icon={Shield} 
                  label="Threat Center" 
                  badge={threatCounts.total}
                  badgeColor={threatCounts.total > 0 ? "bg-red-500" : "bg-slate-600"}
                  defaultOpen={location.pathname.includes('/dns') || location.pathname.includes('/files') || location.pathname.includes('/emails') || location.pathname.includes('/apps')}
                >
                  <SubNavItem 
                    to="/dns" 
                    label="DNS Threats" 
                    badge={threatCounts.dns}
                    badgeColor={threatCounts.dns > 0 ? "bg-red-500/80" : "bg-slate-700"}
                  />
                  <SubNavItem 
                    to="/files" 
                    label="File Threats" 
                    badge={threatCounts.files}
                    badgeColor={threatCounts.files > 0 ? "bg-orange-500/80" : "bg-slate-700"}
                  />
                  <SubNavItem 
                    to="/emails" 
                    label="Email Threats" 
                    badge={threatCounts.emails}
                    badgeColor={threatCounts.emails > 0 ? "bg-yellow-500/80" : "bg-slate-700"}
                  />
                  <SubNavItem 
                    to="/apps" 
                    label="App Threats" 
                    badge={threatCounts.apps}
                    badgeColor={threatCounts.apps > 0 ? "bg-purple-500/80" : "bg-slate-700"}
                  />
                </NavGroup>
                
                {/* Endpoints - Simplified */}
                <NavGroup icon={Monitor} label="Endpoints">
                  <SubNavItem to="/devices" label="Device Management" />
                  <SubNavItem to="/agent" label="Agent Download" />
                </NavGroup>
                
                {/* Threat Intelligence */}
                <NavItem to="/threat-intel" icon={ShieldAlert} label="Threat Intel" />
                
                {/* Team */}
                <NavItem to="/users" icon={Users} label="Team" />
                
                {/* Automation - Expandable */}
                <NavGroup icon={Zap} label="Automation">
                  <SubNavItem to="/automation" label="Default Rules" />
                  <SubNavItem to="/rules" label="Custom Rules" />
                  <SubNavItem to="/webhooks" label="Webhooks" />
                </NavGroup>
                
                {/* Reports */}
                <NavItem to="/reports" icon={FileText} label="Reports" />
              </div>

              {/* Account Section */}
              <div className="mt-6 pt-6 border-t border-slate-800 space-y-1">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-3">
                  Account
                </div>
                
                {/* Settings - Expandable */}
                <NavGroup icon={Settings} label="Settings">
                  <SubNavItem to="/settings" label="General" end={true} />
                  <SubNavItem to="/settings/integrations" label="Integrations" />
                  <SubNavItem to="/billing" label="Billing" />
                </NavGroup>
                
                {/* Referrals */}
                <NavItem to="/referrals" icon={Gift} label="Referrals" />
              </div>
            </>
          )}
        </nav>

        {/* Organization Info */}
        <div className="p-4 border-t border-slate-800">
          {user?.role === 'super_admin' ? (
            <>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">System Admin</div>
              <div className="text-sm font-medium text-white truncate">Global Administrator</div>
              <div className="text-xs text-slate-500 mt-1">Full System Access</div>
            </>
          ) : (
            <>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Organization</div>
              <div className="text-sm font-medium text-white truncate">{tenant?.company_name}</div>
              <div className="text-xs text-slate-500 mt-1">{tenant?.plan} Plan</div>
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl flex items-center justify-between px-6">
          {/* Mobile Logo */}
          <div className="md:hidden flex items-center gap-2">
            <Logo size="sm" />
          </div>

          {/* Search (placeholder) */}
          <div className="hidden md:block" />

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <NotificationCenter />

            {/* Threat Status */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800">
              {threatCounts.total > 0 ? (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-amber-500">{threatCounts.total} Active Threats</span>
                </>
              ) : (
                <>
                  <ShieldAlert className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs font-medium text-emerald-500">Protected</span>
                </>
              )}
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-500">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm text-slate-300">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-800">
                <DropdownMenuItem className="text-slate-300">
                  <span className="text-xs text-slate-500">{user?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-400 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
